<template>
  <config-base class="opc-ze" :saas-mode="saasMode" show-plan-details>
    <template slot="config">
      <div class="opc-ze__mode" v-if="saasModeList.length > 1">
        <div class="opc-ze__mode__inner opc-ze__mode__row">
          <p class="opc-ze__mode__title">{{$t('billing.events.choose_payment_option')}}</p>
          <zm-button class="opc-ze__mode__diff" type="link" @click="showDiffModal">{{$t('billing.events.compare_feature_title')}}</zm-button>
        </div>
        <zm-radio-group class="opc-ze__mode__events-group" v-model="saasMode" aria-label="Choose a payment option">
          <zm-radio v-for="item in saasModeList" :key="item.label" :label="item.label">
            <p>{{ item.name }}</p>
          </zm-radio>
        </zm-radio-group>
      </div>
      <!-- consumption mode -->
      <component
        v-if="saasMode === EventModeMap.PAY_PER_ATTENDEE"
        :ref="EventModeMap.PAY_PER_ATTENDEE"
        :plan-type="planType"
        :is='configConsumptionRowName'
      />
      <!-- subscription mode -->
      <saas-capacity-config-list
        v-if="saasMode === EventModeMap.SUBSCRIPTION"
        :ref="EventModeMap.SUBSCRIPTION"
        :config-plan-type="planType"
        :title="title"
        :config-capacity-key="capacityKey"
        :configRowName='configRowName'
      />
      <webinar-explain-diff-dialog :visible="webinarDialogVisible" @close="closeWebinarDialog" />
      <preview-payment class="opc-ze__preview-payment" />
    </template>
  </config-base>
</template>

<script type="text/jsx">
import _, { map } from 'lodash';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import ConfigList from '@/components/capacity/config-list';
import { BILLING_CYCLE, ZOOM_SASS_ADDONS } from '@/common/constants';
import plansMixin from '@/plans/mx/plan';
import ConfigBase from '@/plans/EVENT_SAAS/config/base';
import planCommonMixin from '@/plans/EVENT_SAAS/mixin';
import { EventModeMap } from '@/plans/EVENT_SAAS/helper/constant';
import ConfigConsumptionRow from '@/plans/EVENT_SAAS/component/config-consumption-row';
import ConfigConsumptionRowV2 from '@/plans/EVENT_SAAS/component/config-consumption-row-v2';
import { isFromZeConsumption } from '@/plans/EVENT_SAAS/helper/utils';
import { getOnlinePlanName } from '@/common/product';
import { plansof } from '@/utils/chain';
import PreviewPayment from '@/views/checkout/payment/preview-payment';
import autoAddMixin from "@/components/capacity/mx/autoAdd";
import { enableNewUIForZS } from '@/common/service/op-switch';
import TwoWayControl from '@/plans/EVENT_SAAS/mixins/two-way-control';
import WebinarExplainDiffDialog from '@/plans/EVENT_SAAS/component/dialog/webinar-explain-diff-dialog.vue';

const SaasCapacityConfigList = {
  mixins: [ConfigList, planCommonMixin, autoAddMixin, enableNewUIForZS ? TwoWayControl : {}],
  props: {
    title: String,
  },
  data () {
    return {
      mx_selectLabel: this.$t('billing.zoom_session.select_label'),
      handleAutoAddMap: {},
    }
  },
  computed: {
    ...mapGetters(['basePlanTotalLicenses', 'getSelectedPlans', 'getTypeGroupSelectedPlans', 'getSelectedBasePlanInfo', 'getCurrentBasePlanInfo', 'hasBoughtDependPlan', 'boughtPlans']),
    currentSelectedBasePlan() {
      return _.get(this.getSelectedBasePlanInfo?.plans, [0]);
    },
    currentBasePlan() {
      return this.getCurrentBasePlanInfo?.plans?.[0] || {};
    },
    boughtCurrentPlans() {
      return plansof(this.boughtPlans).type(this.configPlanType).get();
    },
    // all plans under the current product type
    planList() {
      return this.planMapFlat.filter((p) => {
        const isBoughtCapacity = map(this.boughtCurrentPlans, this.capacityKey).includes(p[this.capacityKey]);
        return p.type === this.configPlanType && p.isSaas && !isBoughtCapacity;
      });
    },
    // all selected plans under the current product type
    selectedPlanList() {
      return this.getTypeGroupSelectedPlans[this.configPlanType]?.filter((p) => p.isSaas) ?? [];
    },
    addCapacityBtnVisible() {
      return false;
    },
    mx_capacityTips() {
      if (enableNewUIForZS) return '';
      return this.$t('billing.events.saas.capacityTips', [this.title]);
    },
    editTotalLimitDesc() {
      return this.$t(
        this.hasBoughtDependPlan ?
          'billing.addon_license_limit_tips' :
          'billing.addon_license_limit_tips_without_contact',
        [this.title, this.$t(this.currentBasePlan.parentPlanNameKey || this.currentBasePlan.typeNameKey)]);
    },
    // override autoAddMixin
    mx_planType() {
      return this.configPlanType;
    }
  },
  methods: {
    ...mapMutations(['updateSelectPlan', 'batchRemovePlan', 'setUpdateBasePlan']),
    ...mapActions(['switchSelectPlan']),
    addDefaultPlan() {
      const defaultCapacity = _.get(this.allCapacityOrderList, [0]);
      const billingCycleList = this.mx_getBillingCycleList(defaultCapacity);
      const defaultPlan = _.find(billingCycleList, (p) => p?.disabled === false);
      const { plan_code, cycleValue } = defaultPlan?.plan || _.get(this.capacityMap, [defaultCapacity, 0], {});

      const cycleDisabled = cycleValue >= 12 && this.mx_annualPlanDisabled?.disabled;
      plan_code && this.updateSelectPlan({ plan_code, cartInfo: { selected_number: cycleDisabled ? 0 : 1 } });
    },
    removePlans() {
      this.batchRemovePlan(this.selectedPlanList);
    },
    mx_getMaxHosts() {
      if (this.mx_canAutoAdd) {
        return this.mx_maxAutoAddHosts
      }
      return this.basePlanTotalLicenses || 999;
    },
    mx_getMonthlyPlan(capacityPlanList = []) {
      const monthlyPlan = _.find(capacityPlanList, (p) => p.billing_cycle === BILLING_CYCLE.MONTHLY);

      const defaultDisabledBillingCycle = {
        disabled: true,
        disabledInnerDesc: this.$t('billing.events.onlyForAttendeesDesc', [100]),
      };

      let annualAddons = plansof(this.boughtPlans).filter(x => x.baseType == this.$context.type).type(...ZOOM_SASS_ADDONS).annual().get();
      if (ZOOM_SASS_ADDONS.includes(this.$context.type)) {
        annualAddons = plansof(this.getSelectedPlans).type(...ZOOM_SASS_ADDONS).annual().get();
      }
      const hasAnnualAddon = !!annualAddons?.length;

      const monthlyTooltips = hasAnnualAddon ? this.$t('billing.planform.can_not_select_monthly', [
        getOnlinePlanName(plansof(this.getSelectedPlans).type(this.$context.type).get(0)),
        getOnlinePlanName(annualAddons[0]),
      ]) : '';

      return {
        plan: monthlyPlan, ...(monthlyPlan ? {
          disabled: hasAnnualAddon,
          tooltips: monthlyTooltips,
        } : { plan: { billing_cycle: BILLING_CYCLE.MONTHLY }, ...defaultDisabledBillingCycle }),
      };
    },
    afterSwitchCapacityHook(oldPlan, newPlan) {
      const selectedBasePlan = this.currentSelectedBasePlan;

      if (newPlan?.billing_cycle === BILLING_CYCLE.ANNUAL && selectedBasePlan?.billing_cycle === BILLING_CYCLE.MONTHLY) {
        const newPlan = _.find(this.planMapFlat, (p) => p.type === selectedBasePlan?.type && p.billing_cycle === BILLING_CYCLE.ANNUAL);

        this.switchSelectPlan([selectedBasePlan.plan_code, newPlan?.plan_code,]);
        // todo by york tracking (sync shopping cart data to form)
        this.setUpdateBasePlan({ update: true, addTime: Date.now() });

        const message = this.$t('billing.common.billing_cycle_auto_update_desc', [
          getOnlinePlanName(selectedBasePlan),
          this.title,
        ]);

        this.$message({
          type: 'info',
          duration: 5000,
          message,
          showClose: true
        });
      }
    },
    handleLicenseChange(license, oldLicense, planInfo) {
      const maxLicense = this.mx_getMaxHosts();
      const shouldShowLicenseAlert = (license >= maxLicense && maxLicense !== 999)
      this.mx_licenseAlert = shouldShowLicenseAlert ? this.editTotalLimitDesc : "";
      if (this.mx_canAutoAdd) {
        this.handleAutoAdd(license, oldLicense, planInfo);
      }
    },
    /**
     * plan license change sync base plan license
     * @param {number} newLicense newLicense
     * @param {number} oldLicense oldLicense
     * @param {object} planInfo
     * @param {number} planInfo.capacity
     * @param {string} planInfo.billingCycle
     */
    handleAutoAdd(newLicense, oldLicense, { capacity }) {
      if (!this.handleAutoAddMap[capacity]) {
        const self = this;
        this.handleAutoAddMap[capacity] =  _.debounce((newLicense, oldLicense, { capacity }) => {
          // cancel switch plan, reset license
          self.mx_handlePlanLicenseChange(newLicense).catch((previousLicense) => {
            const configRowInstance = _.get(self.$refs, [`configRow${capacity}`, '0']);
            if (configRowInstance) {
              configRowInstance.changeLicense(previousLicense);
            }
          });
        }, 300);
      }
      this.handleAutoAddMap[capacity](newLicense, oldLicense, { capacity });
    }
  },
};

export default {
  mixins: [plansMixin, planCommonMixin, autoAddMixin],
  components: {
    WebinarExplainDiffDialog,
    PreviewPayment,
    ConfigConsumptionRow,
    ConfigConsumptionRowV2,
    ConfigBase: {
      mixins: [ConfigBase, planCommonMixin],
      computed: { ...mapGetters(['planMapFlat']) },
      mounted() {
        const saasPlan = _.filter(this.planMapFlat, (p) => p.type === this.planType && p.isSaas);

        if (saasPlan?.length === 0 || isFromZeConsumption()) {
          this.title = `${this.$t('billing.planform.title.zoom_events')} ${this.$t('billing.events.ppa.title')}`;
        }
      }
    },
    SaasCapacityConfigList,
  },
  data() {
    return {
      isExpanded: true,
      hasSelectedPlan: false,
      saasMode: '',
      saasModeList: [],
      EventModeMap,
      webinarDialogVisible: false
    };
  },
  computed: {
    ...mapGetters(['planMapFlat', 'getTypeGroupSelectedPlans']),
    planList() {
      return this.planMapFlat.filter((p) => p.type === this.planType);
    },
    saasPlans() {
      return _.filter(this.planList, (p) => p.isSaas);
    },
    consumptionPlans() {
      return _.filter(this.planList, (p) => p.isPPA);
    },
    isSassPlansAvailable() {
      const not = this.saasPlans?.length === 0 || isFromZeConsumption();
      return !not;
    },
    selectedPlanList() {
      return this.getTypeGroupSelectedPlans[this.planType] ?? [];
    },
    selectedSaasPlanList() {
      return _.filter(this.selectedPlanList, (p) => p.isSaas);
    },
    selectedPPAPlanList() {
      return _.filter(this.selectedPlanList, (p) => p.isPPA);
    },
    configRowName() {
      if (enableNewUIForZS) {
        return 'CapacityConfigRowV2';
      }
      return 'CapacityConfigRow';
    },
    configConsumptionRowName() {
      if (enableNewUIForZS) {
        return 'ConfigConsumptionRowV2';
      }
      return 'ConfigConsumptionRow';
    }
  },
  watch: {
    saasMode(nv, ov) {
      if (nv && ov) {
        this.$refs[ov]?.removePlans?.();
        this.$nextTick(() => {
          this.$refs[nv]?.addDefaultPlan?.();
        });

        // auto-add: remove bought base plan from cart when switch to PPA mode
        if (this.mx_canAutoAdd && nv === EventModeMap.PAY_PER_ATTENDEE) {
          this.mx_removeBoughtBasePlan();
        }
      }
    }
  },
  methods: {
    ...mapMutations(['updateSelectPlan', 'batchRemovePlan']),
    ...mapActions(['switchSelectPlan']),
    showDiffModal() {
      this.webinarDialogVisible = true
    },
    initSaasMode() {
      if (!ZOOM_SASS_ADDONS.includes(this.$context.type)) {
        this.saasModeList = [
          {
            label: EventModeMap.PAY_PER_ATTENDEE,
            name: this.$t('billing.events.ppa.title'),
          },
        ];
      }
      if (this.isSassPlansAvailable) {
        this.saasModeList.push({
          label: EventModeMap.SUBSCRIPTION,
          name: this.$t('billing.events.saas.title'),
        });
      }

      if (this.selectedSaasPlanList.length) {
        this.saasMode = EventModeMap.SUBSCRIPTION;
      } else if (this.selectedPPAPlanList.length) {
        this.saasMode = EventModeMap.PAY_PER_ATTENDEE;
      }
    },
    closeWebinarDialog() {
      this.webinarDialogVisible = false
    }
  },
  created() {
    this.initSaasMode();
  },
};
</script>

<style lang="scss">
@import '@zoom/billing-responsive/styles/responsive.scss';

.opc-ze {
  &__head {
    &__desc {
      color: #666487;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 150%;
    }

    &__action {
      &.zm-button {
        padding: 0px;
        margin-left: 4px;
        font-size: 14px;
        font-style: normal;
        font-weight: 500;
        line-height: 120%;
      }

      .zm-button__slot::first-letter {
        text-transform: capitalize;
      }
    }
  }

  &__mode {
    margin-bottom: 24px;

    &__inner {
      display: flex;
      align-items: center;
      gap: 24px;
      margin-bottom: 8px;
    }

    @include media(mobile) {
      .opc-ze__mode__inner.opc-ze__mode__row {
        flex-direction: column;
        gap: 0;
        align-items: flex-start;
      }
    }

    &__title {
      font-size: 18px;
      font-weight: 700;
      line-height: 27px;
      text-align: left;
    }

    &__diff {
      font-size: 14px;
      font-style: normal;
      font-weight: 600;
      line-height: 120%;
    }

    &__events-group {
      &.zm-radio-group {
        .zm-radio__inner {
          top: 2px;
        }

        .zm-radio__label--inner {
          text-transform: capitalize;
        }
      }
    }
  }

  &__preview-payment {
    margin-top: 24px;
  }

}
</style>
